import { BarChart3 } from 'lucide-react';
import { OrgAdminEmptyState } from '@/components/molecules/org-admin/OrgAdminStates';
import { OverviewStatTiles } from '@/components/molecules/org-admin/overview/OverviewStatTiles';
import { TimeSavedEstimate } from '@/components/molecules/org-admin/overview/TimeSavedEstimate';
import type { OrgOverview } from '@/services/org-admin/overview';
import { formatTokenCount } from '../usageFormat';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import { formatCount } from './overviewLayout';

/**
 * "What are the results?" — the countable outputs, and an editable estimate of what they were worth.
 */
export interface ResultsSectionProps {
  data: OrgOverview | null;
  /** Prose-ready and already carrying its own determiner — "this month", "the last 30 days". */
  windowLabel: string;
  isLoading: boolean;
}

/**
 * The time-saved estimate is deferred — flip to re-enable.
 *
 * A flag rather than commented-out JSX: commented markup stops type-checking, so `TimeSavedEstimate`,
 * `OrgAdminEmptyState`, `BarChart3` and the `windowLabel` prop all read as unused and fail the build
 * under `noUnusedLocals`. Same idiom as `SHOW_EDIT_BUDGET` in `MemberUsageTable`.
 */
const SHOW_TIME_SAVED = false;

export function ResultsSection({ data, windowLabel, isLoading }: ResultsSectionProps) {
  return (
    <Section title="What are the results?">
      <OverviewStatTiles
        headlines={
          data
            ? [
                {
                  id: 'runs-done',
                  label: 'Runs',
                  value: formatCount(data.results.runs),
                  changeRatio: null,
                  risePolarity: 'good',
                },
                /*
                 * There is no "Succeeded" tile, and there must not be one.
                 *
                 * It rendered `runs - failed` under that label, which the service report cannot
                 * support: `run_count` spans cancelled and in-flight runs, so the subtraction yields
                 * "not in the failed bucket", not "succeeded". Analysis already refused to make that
                 * claim about the same arithmetic, so the panel was asserting success on this tab and
                 * declining to on that one — to the same admin, about the same runs.
                 *
                 * Runs and Failed are both real fields and together carry the whole answer. Restore a
                 * success tile only when the API ships `succeeded_count`.
                 */
                {
                  id: 'failed',
                  label: 'Failed',
                  value: formatCount(data.results.failed),
                  changeRatio: null,
                  // Up is bad here for the same reason it is on spend.
                  risePolarity: 'bad',
                },
                {
                  id: 'tokens-in',
                  label: 'Tokens in',
                  value: formatTokenCount(data.results.inputTokens),
                  changeRatio: null,
                  risePolarity: 'neutral',
                },
                {
                  id: 'tokens-out',
                  label: 'Tokens out',
                  value: formatTokenCount(data.results.outputTokens),
                  changeRatio: null,
                  risePolarity: 'neutral',
                },
              ]
            : []
        }
        isLoading={isLoading}
        ariaLabel="Output totals"
      />
      {SHOW_TIME_SAVED && (
  <div className="mt-3">
          <section className="oa-panel" aria-labelledby="oa-timesaved-title">
            <header className="oa-toolbar py-2.5">
              <h3 id="oa-timesaved-title" className="oa-title truncate">
                Estimated time saved
              </h3>
              <p className="oa-meta mt-0.5">
                Real run counts × minutes-per-run assumptions you control.
              </p>
            </header>
            {data?.timeSaved.length ? (
              <TimeSavedEstimate rows={data.timeSaved} windowLabel={`In ${windowLabel}`} />
            ) : (
              <OrgAdminEmptyState
                icon={BarChart3}
                title="Nothing to estimate yet"
                description="This needs at least one run that did not fail."
                className="min-h-[140px]"
              />
            )}
          </section>
        </div>
      )}
    </Section>
  );
}
